import { Drawer, IconButton, Typography, Divider, Grid, Chip, Tooltip } from "@mui/material";
import React, { useState } from "react";
import {
  Close,
  PictureAsPdf,
  Image as ImageIcon,
  InsertDriveFile,
  Visibility,
} from "@mui/icons-material";
import { CompanyResource } from "@/graphql/generated/graphql";
import { titleize } from "inflection";
import DocumentsViewerModal from "@/components/DocumentsViewer";
import { formatFileSize } from "@/utils/helperFunctions";
import DateTimeFormatter from "@/shared-components/DateTimeFormatter";

interface DetailsDrawerProps {
  onClose: () => void;
  open: boolean;
  equipment?: CompanyResource;
}

const DetailsDrawer: React.FC<DetailsDrawerProps> = ({ open, onClose, equipment }) => {
  const meta = equipment?.resourceMeta;
  const isVehicle = meta?.resourceCategory?.name === "Vehicle";

  const [viewerUrl, setViewerUrl] = useState<string | null>(null);

  const InfoField = ({ label, value }: { label: string; value?: string | number | null }) => (
    <Grid size={{ xs: 6 }} sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600, textTransform: "uppercase" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || "—"}
      </Typography>
    </Grid>
  );

  const DateField = ({ label, value }: { label: string; value?: string | undefined | null }) => (
    <Grid size={{ xs: 6 }} sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600, textTransform: "uppercase" }}>
        {label}
      </Typography>
      {value ?
        <DateTimeFormatter
          rawDate={value}
        />
        : "—"
      }
    </Grid>
  );

  // Helper to pick the right icon based on filename
  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <PictureAsPdf sx={{ color: '#f44336' }} />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext || '')) return <ImageIcon sx={{ color: '#2196f3' }} />;
    return <InsertDriveFile sx={{ color: '#757575' }} />;
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      variant="persistent"
      sx={{
        width: 600,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 600,
        },
      }}>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ py: 2, px: 3, bgcolor: "grey.50" }}
      >
        <Grid size="grow">
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {titleize(equipment?.name ?? "Resource Details")}
          </Typography>
          <Grid container alignItems="center" spacing={1}>
            {meta?.equipmentType && (
              <Grid>
                <Chip
                  label={titleize(meta.equipmentType)}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Grid>
            )}
            <Grid>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                • {meta?.shortCode || 'No Short Code'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <IconButton onClick={onClose} size="small" sx={{ mb: 1 }}>
          <Close />
        </IconButton>
      </Grid>

      <Divider />

      <Grid
        container
        direction="column"
        sx={{
          p: 3,
          flex: 1,
        }}
      >
        <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 700 }}>
          GENERAL IDENTITY
          <Chip sx={{ ml: 1 }} label={`${meta?.isScheduleable ? "Scheduleable" : "Not Scheduleable"}`} color={`${meta?.isScheduleable ? "success" : "error"}`} size="small" />
        </Typography>
        <Grid container spacing={1} sx={{ mb: 4 }}>
          <InfoField label="Short Code" value={meta?.shortCode} />
          <InfoField label="Equipment Type" value={titleize(meta?.equipmentType ?? "")} />
          {meta?.status && <InfoField label="Status" value={titleize(meta?.status ?? "")} />}
          <InfoField label="Category" value={meta?.resourceCategory?.name} />
        </Grid>

        <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 700 }}>
          SPECIFICATIONS & QUANTITY
        </Typography>
        <Grid container spacing={1} sx={{ mb: 4 }}>
          {isVehicle ? <>
            <InfoField label="Serial Number" value={meta?.serialNumber} />
            <InfoField label="Make / Model" value={meta?.makeModel} />
            <InfoField label="Capacity" value={meta?.capacity} />
            <InfoField label="Max Reach" value={meta?.maxReach} />
            <InfoField label="Fuel Type" value={meta?.fuelType} />
            <Grid size={12}>
              <InfoField label="Weight & Dimensions" value={meta?.weightDimensions} />
            </Grid>
          </> :
            <>
              <InfoField label="Quantity" value={meta?.quantity || 0} />
              <InfoField label="Unit" value={meta?.unit || 0} />
              <InfoField label="Batch Number" value={meta?.batchNumber} />
            </>
          }
        </Grid>

        <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 700 }}>
          MAINTENANCE & COMPLIANCE
        </Typography>
        <Grid container spacing={1} sx={{ mb: 4 }}>
          {isVehicle ? <>
            <DateField label="Next Service" value={meta?.nextServiceDate?.split('-').reverse().join('-')} />
            <DateField label="Insurance Expiry" value={meta?.insuranceExpiryDate?.split('-').reverse().join('-')} />
            <DateField label="Permit Expiry" value={meta?.permitExpiryDate?.split('-').reverse().join('-')} />
          </> :
            <>
              <DateField label="Expiry Date" value={meta?.expiryDate?.split('-').reverse().join('-')} />
            </>}
        </Grid>

        {isVehicle &&
          <>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 700 }}>
              LOGISTICS & CONTACT
            </Typography>
            <Grid container spacing={1} sx={{ mb: 4 }}>
              <InfoField label="Location" value={meta?.currentLocation} />
              <Grid size={12}>
                <InfoField label="Operator Requirements" value={meta?.operatorRequirements} />
              </Grid>
            </Grid>
          </>}

        <Grid container direction="column" spacing={2} sx={{ p: 1 }}>
          {
            meta?.attachments.map((doc, index) => (
              <Grid key={doc.id || index} size={12}>
                <Grid
                  container
                  alignItems="center"
                  spacing={2}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    '&:hover': { bgcolor: 'grey.50' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  {/* Icon Column */}
                  <Grid>
                    {getFileIcon(doc.filename)}
                  </Grid>

                  {/* File Info Column */}
                  <Grid size="grow">
                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                      {doc.filename}
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(doc.byteSize)}
                        </Typography>
                      </Grid>
                      <Grid>
                        <Typography variant="caption" color="text.secondary">•</Typography>
                      </Grid>
                      <Grid>
                        <Typography variant="caption" color="text.secondary">
                          Added <DateTimeFormatter
                            rawDate={doc.createdAt}
                          />
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Actions Column */}
                  <Tooltip title="View Document">
                    <IconButton
                      color="primary"
                      onClick={() => setViewerUrl(doc.previewUrl)}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            ))
          }
        </Grid>
      </Grid>

      {viewerUrl && (
        <DocumentsViewerModal
          urls={[viewerUrl]}
          handleClose={() => setViewerUrl(null)}
        />
      )}
    </Drawer >
  );
};

export default DetailsDrawer;
